/**
 * Services List Page
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  InputAdornment,
  Card,
  IconButton,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Image as ImageIcon,
  Visibility as VisibilityIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
} from '@mui/icons-material';
import { PageHeader, DataTable, Column, ConfirmDialog } from '../../components/common';
import {
  useGetServicesQuery,
  useDeleteServiceMutation,
  useUpdateServiceMutation,
} from '../../store/api/servicesApi';
import { Service } from '../../types/service';
import moment from 'moment';
import toast from 'react-hot-toast';

const ServicesListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  const { data, isLoading, error } = useGetServicesQuery({
    page: page + 1,
    page_size: rowsPerPage,
    search: search || undefined,
  });

  const [deleteService, { isLoading: isDeleting }] = useDeleteServiceMutation();
  const [updateService] = useUpdateServiceMutation();
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const handleToggleActive = async (service: Service) => {
    setTogglingId(service.id);
    try {
      const formData = new FormData();
      formData.append('is_active', (!service.is_active).toString());
      await updateService({ id: service.id, data: formData }).unwrap();
      toast.success(
        service.is_active
          ? 'Service deactivated successfully'
          : 'Service activated successfully'
      );
    } catch (err: any) {
      toast.error(err?.data?.message || 'Failed to update service status');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedService) return;

    try {
      await deleteService(selectedService.id).unwrap();
      toast.success(t('services.toast.deleted'));
      setDeleteDialog(false);
      setSelectedService(null);
    } catch (err: any) {
      toast.error(err?.data?.message || t('services.toast.deleteFailed'));
    }
  };

  const columns: Column<Service>[] = [
    {
      id: 'icon',
      label: t('services.cols.icon'),
      minWidth: 80,
      render: (row) => (
        <Avatar
          src={row.icon || undefined}
          alt={row.name}
          variant="rounded"
          sx={{ width: 40, height: 40 }}
        >
          <ImageIcon />
        </Avatar>
      ),
    },
    {
      id: 'name',
      label: t('services.fields.name'),
      minWidth: 150,
      render: (row) => (
        <Box sx={{ fontWeight: 600, color: 'primary.main' }}>{row.name}</Box>
      ),
    },
    {
      id: 'slug',
      label: t('services.fields.slug'),
      minWidth: 150,
    },
    {
      id: 'description',
      label: t('services.fields.description'),
      minWidth: 200,
      render: (row) => (
        <Box
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 300,
          }}
        >
          {row.description}
        </Box>
      ),
    },
    {
      id: 'subservices_count',
      label: t('services.cols.subservices'),
      minWidth: 100,
      align: 'center',
      render: (row) => (
        <Chip label={row.subservices_count} size="small" color="primary" />
      ),
    },
    {
      id: 'order',
      label: t('services.fields.order'),
      minWidth: 80,
      align: 'center',
    },
    {
      id: 'uses_dual_pricing',
      label: t('services.cols.pricing'),
      minWidth: 110,
      render: (row) => (
        <Chip
          label={row.uses_dual_pricing ? t('services.pricing.dual') : t('services.pricing.single')}
          size="small"
          color={row.uses_dual_pricing ? 'info' : 'default'}
        />
      ),
    },
    {
      id: 'is_active',
      label: t('services.cols.status'),
      minWidth: 100,
      render: (row) => (
        <Chip
          label={row.is_active ? t('services.status.ACTIVE') : t('services.status.INACTIVE')}
          size="small"
          color={row.is_active ? 'success' : 'default'}
        />
      ),
    },
    {
      id: 'created_at',
      label: t('services.cols.created'),
      minWidth: 120,
      render: (row) => moment(row.created_at).format('MMM DD, YYYY'),
    },
    {
      id: 'actions',
      label: t('common.actions'),
      minWidth: 150,
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => navigate(`/services/${row.id}`)}
            color="info"
            title={t('services.buttons.viewDetails')}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => navigate(`/services/${row.id}/edit`)}
            color="primary"
            title={t('common.edit')}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleToggleActive(row)}
            disabled={togglingId === row.id}
            color={row.is_active ? 'success' : 'default'}
            title={row.is_active ? 'Deactivate' : 'Activate'}
          >
            {row.is_active
              ? <ToggleOnIcon fontSize="small" />
              : <ToggleOffIcon fontSize="small" />}
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('services.title')}
        subtitle={t('services.subtitle')}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.services') },
        ]}
     
      />

      {/* Filters */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder={t('services.filters.searchPlaceholder')}
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Card>

      {/* Table */}
      <DataTable
        columns={columns}
        data={data?.results || []}
        loading={isLoading}
        error={error ? t('services.loadError') : null}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={data?.count || 0}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        emptyMessage={t('services.empty')}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog}
        title={t('services.dialog.deleteTitle')}
        message={t('services.dialog.deleteMessage', { name: selectedService?.name })}
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteDialog(false);
          setSelectedService(null);
        }}
        loading={isDeleting}
      />
    </Box>
  );
};

export default ServicesListPage;

